/**
 * Pont SPA ↔ shell macOS natif (Callizy.app).
 *
 * Le wrapper macOS (WKWebView) injecte `window.isCallizyDesktop = true` et
 * `window.callizyMacBridge.send(type, payload)` au document-start. Ce module
 * expose une API stable côté SPA et fait le POST du token APNs reçu du Swift
 * vers le backend (le shell ne connaît pas le JWT, c'est la SPA qui l'a).
 *
 * Hors WKWebView (Safari, Chrome, Firefox), tout est no-op.
 */
import { api } from './api'

declare global {
  interface Window {
    isCallizyDesktop?: boolean
    callizyMacBridge?: {
      send: (type: string, payload?: unknown) => void
    }
    callizyDesktop?: {
      onApnsToken: (token: string) => void
      openCall: (number: string) => void
    }
    __callizyPendingApnsToken?: string
    __callizyPendingCallNumber?: string
  }
}

export function isDesktopShell(): boolean {
  return typeof window !== 'undefined' && window.isCallizyDesktop === true
}

/** Extrait la version de l'app Mac depuis le User-Agent (suffixe injecté par
 *  AppConfig.userAgentSuffix, format "CallizyDesktop/X.Y.Z"). Renvoie null si
 *  on n'est pas dans le shell ou que le UA n'a pas le suffixe attendu. */
export function getDesktopVersion(): string | null {
  if (typeof navigator === 'undefined') return null
  const match = navigator.userAgent.match(/CallizyDesktop\/(\d+(?:\.\d+)+)/)
  return match ? match[1] : null
}

/** Compare deux versions semver simplifiées (ex: "1.0.1" vs "1.1.0"). */
export function isVersionLessThan(a: string, b: string): boolean {
  const aParts = a.split('.').map((n) => Number.parseInt(n, 10) || 0)
  const bParts = b.split('.').map((n) => Number.parseInt(n, 10) || 0)
  const max = Math.max(aParts.length, bParts.length)
  for (let i = 0; i < max; i++) {
    const x = aParts[i] ?? 0
    const y = bParts[i] ?? 0
    if (x < y) return true
    if (x > y) return false
  }
  return false
}

export function notifyDesktop(type: string, payload?: unknown): void {
  if (!isDesktopShell()) return
  try {
    window.callizyMacBridge?.send(type, payload)
  } catch (e) {
    console.warn('[desktopShell] notifyDesktop failed', e)
  }
}

/**
 * Demande au shell macOS d'ouvrir l'URL dans Safari (browser système) au lieu
 * de la WebView. Utilisé pour les flows OAuth (Google, Apple…) qui ont besoin
 * des cookies du browser de l'user pour login en 1 clic.
 *
 * Hors WKWebView : navigue normalement (no-op pour le shell).
 */
export function openExternalUrl(url: string): boolean {
  if (!isDesktopShell()) return false
  notifyDesktop('open-external-url', { url })
  return true
}

let initialized = false

/**
 * À appeler une seule fois après que l'utilisateur soit authentifié.
 * Branche le handler `onApnsToken` que le shell Swift invoque pour
 * transmettre le token APNs Mac, et flush le buffer si le shell avait
 * envoyé le token avant que la SPA soit prête.
 */
export function initDesktopShell(): void {
  if (!isDesktopShell() || initialized) return
  initialized = true

  window.callizyDesktop = {
    onApnsToken: (token: string) => {
      registerApnsMacToken(token).catch((err) => {
        console.error('[desktopShell] APNs Mac token registration failed', err)
      })
    },
    openCall: (number: string) => {
      // Délégué à la route universelle /call?number=... qui sait gérer login,
      // device pas prêt, navigation locale, etc.
      const target = `/call?number=${encodeURIComponent(number)}`
      window.location.href = target
    },
  }

  if (window.__callizyPendingApnsToken) {
    const buffered = window.__callizyPendingApnsToken
    window.__callizyPendingApnsToken = undefined
    window.callizyDesktop.onApnsToken(buffered)
  }

  if (window.__callizyPendingCallNumber) {
    const buffered = window.__callizyPendingCallNumber
    window.__callizyPendingCallNumber = undefined
    window.callizyDesktop.openCall(buffered)
  }

  notifyDesktop('ready')
}

async function registerApnsMacToken(token: string): Promise<void> {
  if (!token) return
  await api.post('/api/user/apns-mac-token/', { token })
  console.info('[desktopShell] APNs Mac token registered')
}
